<template>
  <div class="vt-wrapper">
    <table class="vt-table vt-style-01">
      <colgroup>
        <col class="vt-col-arrow" />
        <col
          v-for="col in columns"
          :key="col.key"
          :class="{ 'vt-col-icon': col.label === '_icon_' }"
        />
        <col class="vt-col-last" />
      </colgroup>
      <thead>
        <tr>
          <th />
          <th
            v-for="col in columns"
            :key="col.key"
            :class="{ 'vt-icon-column': col.label === '_icon_' }"
          >
            {{ col.label === '_icon_' ? '' : col.label }}
          </th>
          <th class="vt-last-col" />
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows">
          <tr :key="row.id" @click="rowClick(row)">
            <td
              class="vt-arrow"
              @mousedown.prevent
              @click.stop="row.hasChildren ? toggle(row.id) : null"
            >
              <!-- Only show chevron if row has children -->
              <template v-if="row.hasChildren">
                <VasionChevronDown v-if="isExpanded(row.id)" />
                <VasionChevronRight v-else />
              </template>
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              :class="{
                'vt-icon-cell': col.label === '_icon_',
                'vt-truncate': col.truncate && shouldTruncate(row[col.key]),
              }"
            >
              <div class="vt-cell-content">
                <slot name="cell" :row="row" :col="col">
                  <template v-if="col.label === '_icon_' && row[col.key]">
                    <component :is="row[col.key]" class="vt-dynamic-icon" />
                  </template>
                  <template v-else-if="col.truncate && row[col.key]">
                    <template v-if="shouldTruncate(row[col.key])">
                      <VasionTooltip :text="row[col.key]">
                        <span class="vt-truncated-text">{{ truncateText(row[col.key]) }}</span>
                      </VasionTooltip>
                    </template>
                    <template v-else>
                      {{ row[col.key] }}
                    </template>
                  </template>
                  <template v-else>
                    {{ row[col.key] }}
                  </template>
                </slot>
              </div>
            </td>
            <td class="vt-last-col">
              <div class="vt-dd">
                <button class="vt-ell" :data-id="row.id" @click.stop="toggleDropdown(row.id)">
                  <VasionEllipsisIcon />
                </button>
                <ul
                  v-if="openDropdownId === row.id"
                  :class="['vt-dd-menu', { 'vt-dd-menu-up': menuDirections[row.id] }]"
                  :style="menuPositions[row.id]"
                  :data-id="row.id"
                >
                  <li v-for="(opt, i) in ellipsisOptions" :key="i">
                    <button class="vt-dd-item" @click.stop="onOptionClick(opt, row)">
                      <component :is="opt.icon" v-if="opt.icon" class="vt-icon" />
                      <span>{{ opt.label }}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr
            v-for="child in isExpanded(row.id) ? childrenCache[row.id] : []"
            :key="child.id"
            class="vt-child"
            @click="childRowClick(child)"
          >
            <td />
            <td
              v-for="col in columns"
              :key="col.key"
              :class="{
                'vt-icon-cell': col.label === '_icon_',
                'vt-child-padded': col.childPadding,
                'vt-truncate': col.truncate && shouldTruncate(child[col.key]),
              }"
            >
              <div class="vt-cell-content">
                <slot name="childCell" :row="child" :col="col" :parent="row">
                  <template v-if="col.label === '_icon_' && child[col.key]">
                    <component :is="child[col.key]" class="vt-dynamic-icon" />
                  </template>
                  <template v-else-if="col.truncate && child[col.key]">
                    <template v-if="shouldTruncate(child[col.key])">
                      <VasionTooltip :text="child[col.key]">
                        <span class="vt-truncated-text">{{ truncateText(child[col.key]) }}</span>
                      </VasionTooltip>
                    </template>
                    <template v-else>
                      {{ child[col.key] }}
                    </template>
                  </template>
                  <template v-else>
                    {{ child[col.key] }}
                  </template>
                </slot>
              </div>
            </td>
            <td class="vt-last-col">
              <div class="vt-dd">
                <button class="vt-ell" :data-id="child.id" @click.stop="toggleDropdown(child.id)">
                  <VasionEllipsisIcon />
                </button>
                <ul
                  v-if="openDropdownId === child.id"
                  :class="['vt-dd-menu', { 'vt-dd-menu-up': menuDirections[child.id] }]"
                  :style="menuPositions[child.id]"
                  :data-id="child.id"
                >
                  <li v-for="(opt, i) in ellipsisChildOptions" :key="i">
                    <button class="vt-dd-item" @click.stop="onOptionClick(opt, child)">
                      <component :is="opt.icon" v-if="opt.icon" class="vt-icon" />
                      <span>{{ opt.label }}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'VasionTreeTable',
  props: {
    childRowClick: { type: Function, default: () => {} },
    columns: { type: Array, required: true },
    ellipsisChildOptions: { type: Array, default: () => [] },
    ellipsisOptions: { type: Array, default: () => [] },
    fetchChildren: { type: Function, required: true },
    rowClick: { type: Function, default: () => {} },
    rows: { type: Array, required: true },
  },
  data() {
    return {
      childrenCache: {},
      expanded: {},
      menuDirections: {},
      menuPositions: {},
      openDropdownId: null,
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      if (!this.openDropdownId) return
      // if click inside any dropdown container, do nothing
      if (event.target.closest('.vt-dd')) return
      this.openDropdownId = null
    },
    isExpanded(id) {
      return !!this.expanded[id]
    },
    onOptionClick(opt, row) {
      const fn = opt.handler || opt.action
      if (fn) fn(row)
      this.openDropdownId = null
    },
    shouldTruncate(text, maxLength = 20) {
      return text && typeof text === 'string' && text.length > maxLength
    },
    async toggle(id) {
      this.openDropdownId = null
      const row = this.rows.find(r => r.id === id)
      if (row && row.hasChildren) {
        if (!this.expanded[id] && !this.childrenCache[id]) {
          const children = await this.fetchChildren(id)
          this.$set(this.childrenCache, id, children)
        }
        this.$set(this.expanded, id, !this.expanded[id])
      }
    },
    toggleDropdown(id) {
      const newId = this.openDropdownId === id ? null : id
      this.openDropdownId = newId
      if (newId) {
        this.$nextTick(() => {
          const btn = this.$el.querySelector(`button[data-id="${id}"]`)
          const menu = this.$el.querySelector(`ul[data-id="${id}"]`)
          if (btn && menu) {
            const rect = btn.getBoundingClientRect()
            const bottom = rect.bottom
            const spaceBelow = window.innerHeight - bottom
            const menuHeight = menu.offsetHeight
            const openUp = spaceBelow < menuHeight
            this.$set(this.menuDirections, id, openUp)

            const menuWidth = menu.offsetWidth
            const spaceRight = window.innerWidth - rect.left
            let left = rect.left
            if (spaceRight < menuWidth) {
              left = rect.right - menuWidth
              if (left < 0) left = 0
            }

            const top = openUp ? rect.top - menuHeight : bottom
            this.$set(this.menuPositions, id, {
              position: 'fixed',
              top: `${top}px`,
              left: `${left}px`,
            })
          }
        })
      }
    },
    truncateText(text, maxLength = 30) {
      if (!text || typeof text !== 'string' || text.length <= maxLength) {
        return text
      }

      const lastSpaceIndex = text.lastIndexOf(' ', maxLength)

      if (lastSpaceIndex === -1 || lastSpaceIndex < maxLength / 2) {
        return text.substring(0, maxLength) + '...'
      }

      return text.substring(0, lastSpaceIndex) + '...'
    },
    async updateChildren(parentId) {
      const parent = this.rows.find(row => row.id === parentId)
      if (!parent) return
      if (!this.expanded[parentId] && !this.childrenCache[parentId]) return

      if (this.childrenCache[parentId]) {
        this.$delete(this.childrenCache, parentId)
      }
      this.toggle(parentId)
      await this.$nextTick()

      const children = await this.fetchChildren(parentId)
      this.$set(this.childrenCache, parentId, children)
      await this.$nextTick()

      this.toggle(parentId)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';
.vt-wrapper {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
}
table.vt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
th,
td {
  padding: 4px;
  border: 1px solid #ddd;
  vertical-align: middle;
}
.vt-col-arrow,
.vt-col-last {
  width: 40px;
}
.vt-child td.vt-child-padded {
  padding-left: 20px;
}
.vt-ell {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 19.2px;
  padding: 4px;
}
.vt-dd {
  position: relative;
  display: inline-block;
}
.vt-dd-menu {
  list-style: none;
  margin: 0;
  padding: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}
.vt-dd-menu-up {
  bottom: 100%;
  top: auto;
  margin-top: 0;
  margin-bottom: 4px;
}
.vt-dd-item {
  display: flex;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  padding: 4px 8px;
  color: #333;
  cursor: pointer;
}
.vt-dd-item + .vt-dd-item {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.vt-dd-item:hover {
  background-color: #ece7ec;
}
.vt-icon {
  margin-right: 8px;
  font-size: 16px;
}
.vt-cell-content {
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
}
table.vt-style-01 {
  width: 100%;
  border-collapse: collapse;
  height: 100%;

  thead th {
    background-color: $table-header-bg;
    color: $on-surface-variant;
    align-items: center;
    position: sticky;
    color: $on-surface-variant;
    font-family: $font-family-book;
    font-style: normal;
    font-weight: bold;
    white-space: nowrap;
    height: 48px;
    border-bottom: 1px solid $outline-variant;
    font-size: 14px;
    line-height: 20px;
  }

  th,
  td {
    height: 48px;
    border: none;
    border-bottom: 1px solid $outline-variant;
    font-size: 14px;
    line-height: 20px;
  }
  td {
    align-items: center;
    flex-direction: row;
    justify-content: flex-start;
    vertical-align: middle;
  }
  tr {
    background-color: $surface-container;
  }

  tbody tr:nth-child(odd) td {
    background-color: $table-body-default-odd-bg;
  }
  tbody tr:hover td {
    background-color: $table-body-default-hover-bg;
    color: $table-body-default-hover-color;
  }
}
.vt-col-icon {
  width: 48px;
}
.vt-icon-column {
  text-align: center;
}
.vt-icon-cell {
  text-align: center;
}
.vt-dynamic-icon {
  font-size: 18px;
  display: inline-block;
  fill: #707273;
}
.vt-truncate .vt-cell-content {
  overflow: hidden;
}
.vt-truncated-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
}
</style>
